import type { KenoBetType, KenoDraw } from '@nexbit/types'
import type { DrawnRound } from './keno-view'

/**
 * The KENO site's calls to the shared API. Same-origin `/api` (the Vite proxy locally, an
 * nginx /api proxy on the server), Bearer token from the launch.
 *
 * The token lives in sessionStorage: it is the session a partner launch hands this tab,
 * it dies with the tab, and it is never put in a URL after /launch has read it.
 */

const TOKEN_KEY = 'keno_token'
const TIER_KEY = 'keno_tier'

function storage(): Storage | null {
  try { return window.sessionStorage } catch { return null }
}

export const session = {
  token: (): string | null => storage()?.getItem(TOKEN_KEY) ?? null,
  setToken: (t: string) => storage()?.setItem(TOKEN_KEY, t),
  clear: () => { storage()?.removeItem(TOKEN_KEY); storage()?.removeItem(TIER_KEY) },
  tier: (): number | null => {
    const v = storage()?.getItem(TIER_KEY)
    const n = v === null || v === undefined ? NaN : Number(v)
    return Number.isInteger(n) && n >= 0 ? n : null
  },
  setTier: (n: number) => storage()?.setItem(TIER_KEY, String(n)),
}

export class ApiError extends Error {
  constructor(public status: number, public code: string, message: string) { super(message) }
}

/** Called once on the first 401 — the page swaps to the reference "You are disconnected." panel. */
let onUnauthorized: () => void = () => {}
export function setUnauthorizedHandler(fn: () => void): void { onUnauthorized = fn }

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = session.token()
  const headers = new Headers(init.headers)
  if (token) headers.set('Authorization', `Bearer ${token}`)
  if (init.body) headers.set('Content-Type', 'application/json')
  const res = await fetch(`/api${path}`, { ...init, headers, cache: 'no-store' })
  if (res.status === 401) {
    onUnauthorized()
    throw new ApiError(401, 'UNAUTHORIZED', 'Session expired')
  }
  const text = await res.text()
  let body: unknown = null
  try { body = text ? JSON.parse(text) : null } catch { /* non-JSON error page */ }
  if (!res.ok) {
    const b = (body ?? {}) as { code?: string; message?: string }
    throw new ApiError(res.status, b.code ?? 'ERROR', b.message ?? `HTTP ${res.status}`)
  }
  return body as T
}

export interface TableState {
  gameId: string
  code: string
  name: string
  gCode: string
  roundDurationSeconds: number
  closeSeconds: number
  payoutRates: Partial<Record<KenoBetType, number>>
  betLimits: { min: number; max: number }[]
  current: { roundId: string; roundNumber: number; openAt: string; closeAt: string; resultAt: string } | null
  previous: { roundId: string; roundNumber: number; resultAt: string; draw: KenoDraw } | null
}

export interface MyBet {
  betId: string
  ticketNo: string | null
  gameId: string
  roundId: string
  table: string
  roundNumber: number
  roundLabel: string
  betType: KenoBetType
  amount: string
  payoutRate: string
  status: 'pending' | 'won' | 'lost' | 'refunded' | 'cancelled'
  winAmount: string | null
  createdAt: string
}

export const api = {
  me: () => request<{ member: { id: string; username: string; displayName: string | null } }>('/auth/me'),
  wallet: () => request<{ balance: string; currency: string }>('/wallet'),
  tables: () => request<{ serverTime: string; tables: TableState[] }>('/keno/tables'),
  roadmap: (gameId: string, limit = 120) =>
    request<DrawnRound[]>(`/keno/roadmap?gameId=${encodeURIComponent(gameId)}&limit=${limit}`),
  history: (gameId: string, date: string, page: number) =>
    request<{ items: DrawnRound[]; total: number; page: number; limit: number }>(
      `/keno/history?gameId=${encodeURIComponent(gameId)}&date=${encodeURIComponent(date)}&page=${page}`,
    ),
  myBets: (openOnly: boolean, page = 1) => request<MyBet[]>(`/keno/bets?open=${openOnly ? 1 : 0}&page=${page}`),
  statement: (month: string) =>
    request<{ day: string; bets: number; stake: string; returned: string }[]>(
      `/keno/statement?month=${encodeURIComponent(month)}`,
    ),
  statementDay: (date: string, page: number) =>
    request<{
      items: { betId: string; ticketNo: string | null; createdAt: string; table: string; roundLabel: string; betType: KenoBetType; amount: string; payoutRate: string; status: string; winAmount: string | null; draw: KenoDraw | null }[]
      total: number; stake: string; returned: string; page: number; limit: number
    }>(`/keno/statement/day?date=${encodeURIComponent(date)}&page=${page}`),
  placeBet: (body: { roundId: string; betType: KenoBetType; amount: string; limitTier: number }, key: string) =>
    request<{ bet: { id: string; ticketNo?: string | null }; newBalance: string }>('/bets', {
      method: 'POST',
      headers: { 'Idempotency-Key': key },
      body: JSON.stringify(body),
    }),
}

/** A fresh idempotency key per submit — a retried submit reuses the one it was given. */
export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

/** Our API's refusal → the reference dictionary key the popover shows. */
export function placeErrorKey(err: unknown): string {
  if (!(err instanceof ApiError)) return 'PLACE_FAIL'
  switch (err.code) {
    case 'BET_CLOSED':
    case 'ROUND_NOT_OPEN':
      return 'ERROR_NMB'
    case 'INSUFFICIENT_BALANCE':
      return 'ERROR_XAMOUNT'
    case 'PRICE_STALE':
      return 'ERROR_GAME_UA'
  }
  if (/maintenance/i.test(err.message)) return 'ERROR_GAME_UA'
  if (/not active/i.test(err.message)) return 'ERROR_209'
  if (/between/i.test(err.message)) return 'ERROR_MAX_TO'
  return 'PLACE_FAIL'
}
